import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../components/api';
import { 
  Building2, Users, CheckCircle2, AlertCircle, 
  Briefcase, ShieldCheck, BarChart3, Percent, 
  Thermometer, Building, Box, ClipboardCheck,
  ChevronDown, XCircle, Loader2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const StatByDepartement = () => {
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedDepartement, setSelectedDepartement] = useState('');
  const [regions, setRegions] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDepartements, setLoadingDepartements] = useState(false);
  const [error, setError] = useState(null);

  // Charger la liste des régions
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setError(null);
        const response = await api.get('collect/regions/');
        setRegions(response.data);
      } catch (error) {
        setError('Erreur lors du chargement des régions. Veuillez réessayer.');
        console.error('Erreur:', error);
      }
    };
    fetchRegions();
  }, []);

  // Charger la liste des départements
  useEffect(() => {
    const fetchDepartements = async () => {
      if (selectedRegion) {
        setLoadingDepartements(true);
        setError(null);
        try {
          const response = await api.get(`collect/departements-by-region/${selectedRegion}/`);
          setDepartements(response.data);
        } catch (error) {
          setError('Erreur lors du chargement des départements. Veuillez réessayer.');
          console.error('Erreur:', error);
        }
        setLoadingDepartements(false);
      } else {
        setDepartements([]);
      }
    };
    fetchDepartements();
  }, [selectedRegion]);

  // Charger les statistiques
  useEffect(() => {
    const fetchStats = async () => {
      if (selectedDepartement) {
        setLoading(true);
        setError(null);
        try {
          const response = await api.get(`collect/stats-departement/${selectedDepartement}/`);
          setStats(response.data);
        } catch (error) {
          setError('Erreur lors du chargement des statistiques. Veuillez réessayer.');
          console.error('Erreur:', error);
        }
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedDepartement]);

  // Gestion des erreurs
  const ErrorMessage = ({ message }) => (
    <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-700">{message}</p>
        </div>
      </div>
    </div>
  );

  // Style personnalisé pour les selects
  const selectClassName = `
    block w-full rounded-lg border-gray-300 bg-white px-4 py-3
    shadow-sm transition-all duration-200
    focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50
    disabled:bg-gray-100 disabled:cursor-not-allowed
    appearance-none
    relative
  `;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && <ErrorMessage message={error} />}

          {/* Sélecteurs améliorés */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionnez une région
              </label>
              <div className="relative">
                <select
                  value={selectedRegion}
                  onChange={(e) => {
                    setSelectedRegion(e.target.value);
                    setSelectedDepartement('');
                    setStats(null);
                  }}
                  className={selectClassName}
                >
                  <option value="">Choisir une région</option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.nom}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionnez un département
              </label>
              <div className="relative">
                <select
                  value={selectedDepartement}
                  onChange={(e) => setSelectedDepartement(e.target.value)}
                  className={selectClassName}
                  disabled={!selectedRegion || loadingDepartements}
                >
                  <option value="">
                    {loadingDepartements 
                      ? "Chargement des départements..." 
                      : "Choisir un département"
                    }
                  </option>
                  {departements.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.nom}
                    </option>
                  ))}
                </select>
                {loadingDepartements ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500 animate-spin" />
                ) : (
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                )}
              </div>
            </div>
          </div>

          {/* Loader principal */}
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-12 w-12 text-emerald-500 animate-spin mb-4" />
              <p className="text-gray-500">Chargement des statistiques...</p>
            </div>
          ) : stats ? (
            <div className="space-y-8">
              {/* Première ligne - Statistiques principales */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Total des dépôts"
                  count={stats.total_depots}
                  icon={Building2}
                  bgColor="bg-gradient-to-br from-violet-500 to-violet-700"
                  subtitle="Dans le département"
                />
                <StatCard
                  title="Dépôts reconnus"
                  count={stats.depots_reconnus}
                  percentage={((stats.depots_reconnus / stats.total_depots) * 100).toFixed(1)}
                  icon={CheckCircle2}
                  bgColor="bg-gradient-to-br from-emerald-500 to-emerald-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="Employés"
                  count={stats.ressources_humaines.total_employes}
                  icon={Users}
                  bgColor="bg-gradient-to-br from-blue-500 to-blue-700"
                  subtitle={`${stats.ressources_humaines.moyenne_employes} en moyenne par dépôt`}
                />
                <StatCard
                  title="Avec Supervision"
                  count={stats.conformite.supervision.total}
                  percentage={stats.conformite.supervision.pourcentage}
                  icon={ShieldCheck}
                  bgColor="bg-gradient-to-br from-orange-500 to-orange-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
              </div>

              {/* Deuxième ligne - Statistiques RH et Infrastructure */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Employés Permanents"
                  count={stats.ressources_humaines.permanents.total}
                  percentage={stats.ressources_humaines.permanents.pourcentage}
                  icon={Briefcase}
                  bgColor="bg-gradient-to-br from-pink-500 to-pink-700"
                  subtitle={`Sur ${stats.ressources_humaines.total_employes} employés`}
                />
                <StatCard
                  title="Port d'insignes"
                  count={stats.ressources_humaines.port_insignes.total}
                  percentage={stats.ressources_humaines.port_insignes.pourcentage}
                  icon={Users}
                  bgColor="bg-gradient-to-br from-cyan-500 to-cyan-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="Avec Climatisation"
                  count={stats.infrastructure.climatisation.total}
                  percentage={stats.infrastructure.climatisation.pourcentage}
                  icon={Thermometer}
                  bgColor="bg-gradient-to-br from-teal-500 to-teal-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="En Location"
                  count={stats.infrastructure.location.total}
                  percentage={stats.infrastructure.location.pourcentage}
                  icon={Building}
                  bgColor="bg-gradient-to-br from-indigo-500 to-indigo-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
              </div>

              {/* Troisième ligne - Statistiques supplémentaires */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Avec Réfrigérateur"
                  count={stats.infrastructure.refrigerateur.total}
                  percentage={stats.infrastructure.refrigerateur.pourcentage}
                  icon={Box}
                  bgColor="bg-gradient-to-br from-amber-500 to-amber-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="Connaissance Arrêté"
                  count={stats.conformite.connaissance_arrete.total}
                  percentage={stats.conformite.connaissance_arrete.pourcentage}
                  icon={ClipboardCheck}
                  bgColor="bg-gradient-to-br from-rose-500 to-rose-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
              </div>

              {/* Graphique des communes */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-violet-500" />
                  Répartition par commune
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.depots_par_commune}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-50" />
                      <XAxis dataKey="nom" />
                      <YAxis />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '0.5rem',
                          border: 'none',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="reconnus" 
                        name="Reconnus" 
                        stroke="#10B981" 
                        strokeWidth={3}
                        dot={{ fill: '#10B981', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="non_reconnus" 
                        name="Non Reconnus" 
                        stroke="#F97316" 
                        strokeWidth={3}
                        dot={{ fill: '#F97316', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 mt-8 p-8 bg-white rounded-xl shadow-sm">
              <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p>Sélectionnez une région et un département pour voir les statistiques</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

const StatCard = ({ title, count, percentage, icon: Icon, bgColor, subtitle }) => (
  <div className={`${bgColor} rounded-xl shadow-sm p-6 text-white h-32`}>
    <div className="flex items-start justify-between">
      <div>
        <h3 className="font-medium opacity-90 text-sm">{title}</h3>
        <p className="text-2xl font-bold mt-1">
          {count?.toLocaleString()}
          {percentage !== undefined && ` (${percentage}%)`}
        </p>
        {subtitle && (
          <p className="text-xs mt-1 opacity-80">{subtitle}</p>
        )}
      </div>
      <div className="bg-white/20 p-2 rounded-xl">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default StatByDepartement; 